import { isKeyOf } from "@/helpers/typeHelpers";
import type { Ref } from "vue";
import { computed } from "vue";
import type { HdnRequestedDocument } from "./useLoanApplicationSortedDocuments";
import { getStatus } from "./useLoanApplicationSortedDocuments";

type RequestedDocumentType =
	| "001 Kopie koopcontract"
	| "003 Taxatierapport van de woning"
	| "008 Saldobiljet schuldrest woning(en)"
	| "009 Werkgeversverklaring(en)"
	| "011 Laatste loonafrekening"
	| "024 Kopie identiteitsbewijs"
	| "051 Afschrift(en) privé-rekening"
	| "067 Bewijsstukken vrij besteedbaar vermogen"
	| "147 Waardebepaling huidige woning"
	| "215 Verkoopopdracht"
	| "251 Informatieformulier"
	| "259 Getekend indicatief voorstel"
	| "267 Kopie identiteitsbewijs zonder BSN";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type RequestedDocumentStatus =
	| "01 Aan te leveren"
	| "02 In behandeling"
	| "03 Ontvangen"
	| "04 Akkoord"
	| "06 Voorlopig akkoord"
	| "07 Afgekeurd en opnieuw aan te leveren (zie toelichting)"
	| "08 Vervallen (zie toelichting";

interface SimplifiedDocument {
	label: string;
	icon?: string;
}

interface SimplifiedDocuments {
	missing: SimplifiedDocument[];
	delivered: SimplifiedDocument[];
	expired: SimplifiedDocument[];
	rejected: SimplifiedDocument[];
}

export function useSimplifiedRequestedDocuments(
	documents: Ref<HdnRequestedDocument[]>,
) {
	// associates document type to mui icon name
	const typeToIconMap: Partial<Record<RequestedDocumentType, string>> = {
		"001 Kopie koopcontract": "contract",
		"011 Laatste loonafrekening": "receipt",
		"267 Kopie identiteitsbewijs zonder BSN": "id_card",
	};

	// get icon associated with document
	const getIcon = (document: HdnRequestedDocument) => {
		return isKeyOf(typeToIconMap, document.documentType)
			? typeToIconMap[document.documentType]
			: "description";
	};

	// utilities to filter documents
	const withStatus = (document: HdnRequestedDocument) =>
		Boolean(getStatus(document));
	const hasStatus = (doc: HdnRequestedDocument, statuses: string[]) =>
		statuses.includes(getStatus(doc)?.status ?? "");
	const isMissing = (document: HdnRequestedDocument) =>
		hasStatus(document, ["01 Aan te leveren"]);
	const isDelivered = (document: HdnRequestedDocument) =>
		hasStatus(document, [
			"02 In behandeling",
			"03 Ontvangen",
			"04 Akkoord",
			"06 Voorlopig akkoord",
		]);
	const isExpired = (document: HdnRequestedDocument) =>
		hasStatus(document, ["08 Vervallen (zie toelichting)"]);
	const isRejected = (document: HdnRequestedDocument) =>
		hasStatus(document, [
			"07 Afgekeurd en opnieuw aan te leveren (zie toelichting)",
		]);

	// utility to get document type without numerical code
	const getSimpleName = (document: HdnRequestedDocument) =>
		document.documentType.slice(3);

	// a simplified document is just label and icon
	const simplifyDocument = (document: HdnRequestedDocument) => ({
		label: getSimpleName(document),
		icon: getIcon(document),
	});

	const simplified = computed<SimplifiedDocuments>(() => {
		const filtered = documents.value.filter(withStatus);
		return {
			expired: filtered.filter(isExpired).map(simplifyDocument),
			delivered: filtered.filter(isDelivered).map(simplifyDocument),
			missing: filtered.filter(isMissing).map(simplifyDocument),
			rejected: filtered.filter(isRejected).map(simplifyDocument),
		};
	});

	return { simplified };
}
